import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import './Bewerbungsmappe.css';

// Schritt 2: Ordner (Hover-Animation) + geoeffnete Mappe mit Kontaktdaten,
// kopierbarem E-Mail-Text (inkl. Betreff) und zwei Dokument-Kacheln, die man
// per Drag-and-Drop auf den Desktop ziehen kann.
//
// Datenquelle Kontakt: Ansprechpartner kommt aus dem Backend
// (empfaenger_ansprechperson). E-Mail/Telefon/WhatsApp zieht der Browser
// vorerst aus dem gespeicherten Anzeigentext (stelle.text). Sobald der
// Analyse-Agent diese Felder liefert, werden die Backend-Werte bevorzugt.
// ponytail: einfache Regex-Extraktion. Ungewoehnlich versteckte Angaben kann
// sie uebersehen -> spaeter Agent-Extraktion im Backend (Handoff an Kollegen).

function ersterTreffer(text, regex) {
  const m = (text || '').match(regex);
  return m ? m[0].trim() : '';
}

function extrahiereKontakt(text) {
  const t = text || '';
  const email = ersterTreffer(t, /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  // Deutsche Telefon-/Handynummer; bounded, damit nicht ueber Zeilen hinweg Muell gegriffen wird.
  const nummerRe = /(?:\+49[\s-]?|0)[\d][\d\s/().-]{5,16}\d/;
  const telefon = ersterTreffer(t, nummerRe);
  // WhatsApp nur, wenn das Wort vorkommt -> dann die naechste Nummer dahinter.
  let whatsapp = '';
  const idx = t.toLowerCase().indexOf('whatsapp');
  if (idx >= 0) whatsapp = ersterTreffer(t.slice(idx, idx + 80), nummerRe);
  return { email, telefon, whatsapp };
}

// Bevorzugt Backend-Feld, faellt auf Browser-Extraktion zurueck.
function kontaktDaten(stelle) {
  const ausText = extrahiereKontakt(stelle?.text);
  return {
    ansprech: (stelle?.empfaenger_ansprechperson || '').trim(),
    email: (stelle?.empfaenger_email || ausText.email || '').trim(),
    telefon: (stelle?.empfaenger_telefon || ausText.telefon || '').trim(),
    whatsapp: (stelle?.empfaenger_whatsapp || ausText.whatsapp || '').trim(),
  };
}

// stelle.name ist oft "Firma – Position". ponytail: am ersten Gedankenstrich
// trennen; bei seltsamen Firmennamen mit Bindestrich ggf. ungenau.
function firmaUndPosition(stelle) {
  const name = (stelle?.name || '').trim();
  const teile = name.split(/\s[–-]\s/);
  return {
    firma: (teile[0] || name).trim(),
    position: teile.length > 1 ? teile.slice(1).join(' – ').trim() : '',
  };
}

// "mehmet_yüksel" -> "Mehmet Yüksel"
function schoenerName(slug) {
  return (slug || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Chromium zieht eine Datei nur dann auf den Desktop, wenn die DownloadURL eine
// data:-Adresse ist — blob:-Adressen werden beim Drag nach aussen blockiert.
// Darum das fertige PDF einmal in eine eingebettete data:-Adresse umwandeln.
async function blobUrlZuDataUrl(url) {
  try {
    const blob = await fetch(url).then((r) => r.blob());
    return await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

const CopyIcon = ({ check }) =>
  check ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );

// Mini-Grafiken auf den Dokument-Kacheln, damit man auf einen Blick erkennt,
// was es ist: Lebenslauf (Foto oben links + Zeilen) vs. Anschreiben (Datum,
// Anrede, Absaetze, Unterschrift). Teal-Linien, Amber-Akzent (Design-Tokens).
const IconLebenslauf = () => (
  <svg className="mk-doc-art" viewBox="0 0 64 84" fill="none" aria-hidden="true">
    <circle cx="20" cy="22" r="7" fill="var(--cv-amber, #FFBC00)" />
    <rect x="31" y="18" width="20" height="3.2" rx="1.6" fill="rgba(0,77,64,0.55)" />
    <rect x="31" y="25" width="13" height="3.2" rx="1.6" fill="rgba(0,77,64,0.28)" />
    <rect x="13" y="37" width="38" height="2" rx="1" fill="rgba(0,77,64,0.18)" />
    <rect x="13" y="45" width="38" height="3" rx="1.5" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="53" width="38" height="3" rx="1.5" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="61" width="30" height="3" rx="1.5" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="69" width="22" height="3" rx="1.5" fill="rgba(0,77,64,0.22)" />
  </svg>
);
const IconAnschreiben = () => (
  <svg className="mk-doc-art" viewBox="0 0 64 84" fill="none" aria-hidden="true">
    <rect x="35" y="15" width="16" height="3" rx="1.5" fill="rgba(0,77,64,0.28)" />
    <rect x="13" y="26" width="26" height="3.2" rx="1.6" fill="rgba(0,77,64,0.5)" />
    <rect x="13" y="35" width="38" height="2.6" rx="1.3" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="41" width="38" height="2.6" rx="1.3" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="47" width="34" height="2.6" rx="1.3" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="55" width="38" height="2.6" rx="1.3" fill="rgba(0,77,64,0.22)" />
    <rect x="13" y="61" width="28" height="2.6" rx="1.3" fill="rgba(0,77,64,0.22)" />
    <path d="M14 73c3-4 5 2 8-1s4 2 7-1" stroke="var(--cv-amber, #FFBC00)"
      strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

function CopyButton({ wert, className = '' }) {
  const [kopiert, setKopiert] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(wert);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1400);
    } catch { /* Clipboard nicht verfuegbar -> still ignorieren */ }
  };
  return (
    <button
      type="button"
      className={`mk-copy${kopiert ? ' mk-copy--ok' : ''} ${className}`}
      onClick={onCopy}
      title="Kopieren"
      aria-label="Kopieren"
    >
      <CopyIcon check={kopiert} />
    </button>
  );
}

function MappeOffen({ stelle, onClose, onDownloadCV, onDownloadAN, onBlobCV, onBlobAN, cvLaedt, anLaedt, onAlsAbgeschickt, langsam }) {
  const panelRef = useRef(null);
  // Wurde der E-Mail-Knopf gedrückt? Erst danach erscheint der bewusste
  // "Als abgeschickt markieren"-Schritt.
  const [mailGestartet, setMailGestartet] = useState(false);
  // Nach dem Markieren den Knopf gegen eine ruhige Bestätigung tauschen.
  const [istMarkiert, setIstMarkiert] = useState(stelle?.bewerbungs_status === 'abgeschickt');
  // Fertige PDFs (als Blob-URL) fuer Drag-and-Drop auf den Desktop.
  // ponytail: beide werden beim Oeffnen im Hintergrund erzeugt -> zwei Backend-
  // Aufrufe pro Oeffnen. Bei Bedarf spaeter cachen/erst bei Hover erzeugen.
  const [pdf, setPdf] = useState({ cv: null, an: null });
  // Anrede-Geschlecht: null = neutral ("/r"), 'herr' oder 'frau'. Wirkt nur, wenn
  // ein Ansprechpartner-Name bekannt ist — ohne Namen bleibt "Damen und Herren".
  const [geschlecht, setGeschlecht] = useState(null);

  const { ansprech, email, telefon, whatsapp } = kontaktDaten(stelle);
  const { firma, position } = firmaUndPosition(stelle);
  const hatKontakt = email || telefon || whatsapp;
  const hatAN = !!stelle?.an_rohtext;
  const absender = schoenerName(stelle?.profil_name);
  // Falls der erkannte Ansprechpartner schon "Herr"/"Frau" enthält, das vorne abschneiden,
  // damit nicht "Sehr geehrter Herr Frau Lena" entsteht.
  const ansprechName = ansprech.replace(/^(herrn?|frau)\s+/i, '');
  const anrede = !ansprech
    ? 'Sehr geehrte Damen und Herren,'
    : geschlecht === 'herr' ? `Sehr geehrter Herr ${ansprechName},`
    : geschlecht === 'frau' ? `Sehr geehrte Frau ${ansprechName},`
    : `Sehr geehrte/r ${ansprechName},`;
  const betreff = position ? `Bewerbung als ${position}` : 'Bewerbung auf Ihre Stellenausschreibung';
  const stelleNennung = position
    ? `auf die ausgeschriebene Stelle als ${position}`
    : 'auf Ihre ausgeschriebene Stelle';
  const body =
    `${anrede}\n\n` +
    `hiermit bewerbe ich mich ${stelleNennung}. Meinen Lebenslauf und mein ` +
    'Anschreiben finden Sie im Anhang.\n\nMit freundlichen Grüßen' +
    (absender ? `\n${absender}` : '');
  const kompletterText = `${betreff}\n\n${body}`;
  const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(body)}`;

  // Beim Oeffnen die rechte Spalte automatisch ganz nach unten scrollen,
  // damit die geoeffnete Mappe komplett sichtbar ist (kein Hand-Scrollen).
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  // Schliessen nur bei einem bewussten Klick in die leere Flaeche der rechten
  // Spalte — nicht beim Klicken irgendwo sonst im Studio und nicht auf ein
  // Bedienelement (Knopf/Link/Eingabe/Kachel), damit die Mappe nicht staendig
  // ungewollt zugeht.
  useEffect(() => {
    const handler = (e) => {
      if (!panelRef.current || panelRef.current.contains(e.target)) return;
      const inRechterSpalte = e.target.closest?.('.editor-right-column');
      const aufBedienelement = e.target.closest?.(
        'button, a, input, textarea, select, label, [role="button"]'
      );
      if (inRechterSpalte && !aufBedienelement) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // PDFs beim Oeffnen vorbereiten, beim Schliessen Blob-URLs freigeben.
  useEffect(() => {
    let alive = true;
    const urls = [];
    (async () => {
      const cv = await onBlobCV?.();
      if (cv) {
        urls.push(cv.url);
        const dataUrl = await blobUrlZuDataUrl(cv.url);
        if (alive) setPdf((p) => ({ ...p, cv: { ...cv, dataUrl } }));
      }
      if (hatAN) {
        const an = await onBlobAN?.();
        if (an) {
          urls.push(an.url);
          const dataUrl = await blobUrlZuDataUrl(an.url);
          if (alive) setPdf((p) => ({ ...p, an: { ...an, dataUrl } }));
        }
      }
    })();
    return () => { alive = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
    // Nur einmal beim Oeffnen erzeugen (MappeOffen wird beim Schliessen unmounted).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Native Datei-Drag auf den Desktop (DownloadURL -> nur Chrome/Edge).
  // Wichtig: die data:-Adresse nehmen, sonst blockiert Chrome das Ziehen.
  const startDrag = (datei) => (e) => {
    const quelle = datei?.dataUrl;
    if (!quelle) { e.preventDefault(); return; }
    e.dataTransfer.setData('DownloadURL', `application/pdf:${datei.name}:${quelle}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Download aus dem schon vorbereiteten Blob -> passiert sofort im Klick,
  // damit der Browser nicht stattdessen einen Tab oeffnet. Ist das Blob noch
  // nicht fertig, faellt es auf das (neu erzeugende) Original zurueck.
  const ladeDatei = (datei, fallback) => () => {
    if (datei) {
      const a = document.createElement('a');
      a.href = datei.url;
      a.download = datei.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      fallback?.();
    }
  };

  // Beim "Abschicken": beide Dokumente als echten Datei-Download starten (kein
  // neuer Tab), danach darf der mailto-Link normal das Mailprogramm oeffnen.
  // mailto kann selbst keine Anhaenge tragen -> der Nutzer haengt die eben
  // geladenen Dateien aus dem Downloads-Ordner an.
  const abschicken = () => {
    ladeDatei(pdf.cv, onDownloadCV)();
    if (hatAN) ladeDatei(pdf.an, onDownloadAN)();
    setMailGestartet(true);
  };
  return (
    <motion.div
      ref={panelRef}
      className="mappe-open"
      style={{ transformOrigin: 'bottom center' }}
      // Oeffnen: sanft heraufgleiten und entfalten. Auf dem langsamen Pfad startet die Mappe
      // kleiner und tiefer, damit die 1,1 s auch sichtbar als Aufwachsen wahrgenommen werden.
      initial={langsam ? { opacity: 0, scale: 0.62, y: 80 } : { opacity: 0, scale: 0.92, y: 18 }}
      animate={{
        opacity: 1, scale: 1, y: 0,
        transition: { duration: langsam ? 1.1 : 0.4, ease: [0.16, 1, 0.3, 1] },
      }}
      // Schliessen: der Ordner ist schon sichtbar (kein "mode=wait" mehr),
      // die Mappe schrumpft sichtbar nach unten IN den Ordner hinein —
      // weich anlaufend, zum Ende hin beschleunigend, Inhalt blendet etwas
      // spaeter aus, damit das Hineingleiten sichtbar bleibt.
      exit={{
        opacity: 0, scale: 0.08, y: 130,
        transition: { duration: 0.5, ease: [0.5, 0, 0.75, 0], opacity: { duration: 0.4, delay: 0.1 } },
      }}
    >
      <div className="mappe-open-head">
        <span>{firma ? `Bewerbungsmappe für ${firma}` : 'Bewerbungsmappe'}</span>
      </div>

      <div className="mk-rows">
        {/* Ansprechpartner steht schon im Brief -> hier keine eigene Zeile mehr. */}
        {email && (
          <div className="mk-row">
            <span className="mk-label">E-Mail</span>
            <span className="mk-value">{email}</span>
            <CopyButton wert={email} />
          </div>
        )}
        {telefon && (
          <div className="mk-row">
            <span className="mk-label">Telefon</span>
            <span className="mk-value">{telefon}</span>
            <CopyButton wert={telefon} />
          </div>
        )}
        {whatsapp && (
          <div className="mk-row">
            <span className="mk-label">WhatsApp</span>
            <span className="mk-value">{whatsapp}</span>
            <CopyButton wert={whatsapp} />
          </div>
        )}
        {!hatKontakt && (
          <div className="mk-hint">
            Keine Kontaktdaten gefunden — vermutlich Bewerbung übers Portal.
          </div>
        )}
      </div>

      <div className="mk-island">
        <div className="mk-textblock">
          <div className="mk-letter">
            <div className="mk-betreff-row">
              <div className="mk-betreff">{betreff}</div>
              <div className="mk-betreff-tools">
                <CopyButton wert={betreff} className="mk-copy-betreff" />
                {/* Anrede-Umschalter nur, wenn ein Ansprechpartner gefunden wurde */}
                {ansprech && (
                  <div className="mk-anrede-switch" role="group" aria-label="Anrede wählen">
                    <button
                      type="button"
                      aria-label="Herr"
                      className={`mk-anrede-opt${geschlecht === 'herr' ? ' is-on' : ''}`}
                      onClick={() => setGeschlecht((g) => (g === 'herr' ? null : 'herr'))}
                    >
                      <span className="mk-anrede-text">Herr</span>
                      <span className="mk-anrede-sym" aria-hidden="true">♂</span>
                    </button>
                    <button
                      type="button"
                      aria-label="Frau"
                      className={`mk-anrede-opt${geschlecht === 'frau' ? ' is-on' : ''}`}
                      onClick={() => setGeschlecht((g) => (g === 'frau' ? null : 'frau'))}
                    >
                      <span className="mk-anrede-text">Frau</span>
                      <span className="mk-anrede-sym" aria-hidden="true">♀</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="mk-divider" />
            <p className="mk-text">{body}</p>
          </div>
          <CopyButton wert={kompletterText} className="mk-copy-letter" />
        </div>
      </div>

      {/* Untere Aktionsleiste: Dokument-Kacheln links, E-Mail-Knopf rechts.
          Ohne E-Mail (kein Knopf) stehen die Kacheln mittig. */}
      <div className={`mk-actions${email ? '' : ' mk-actions--solo'}`}>
        <div className="mk-docs">
          <div
            className={`mk-doc mk-doc-cv${pdf.cv ? ' is-ziehbar' : ''}`}
            draggable={!!pdf.cv}
            onDragStart={startDrag(pdf.cv)}
            onClick={ladeDatei(pdf.cv, onDownloadCV)}
            role="button"
            tabIndex={0}
          >
            <span className="mk-doc-sheet" aria-hidden="true"><IconLebenslauf /></span>
            <span className="mk-doc-label">{cvLaedt ? 'Erstelle…' : 'Lebenslauf'}</span>
          </div>
          <div
            className={`mk-doc mk-doc-an${pdf.an ? ' is-ziehbar' : ''}${!hatAN ? ' is-aus' : ''}`}
            draggable={!!pdf.an}
            onDragStart={startDrag(pdf.an)}
            onClick={hatAN ? ladeDatei(pdf.an, onDownloadAN) : undefined}
            role="button"
            tabIndex={0}
          >
            <span className="mk-doc-sheet" aria-hidden="true"><IconAnschreiben /></span>
            <span className="mk-doc-label">{anLaedt ? 'Erstelle…' : 'Anschreiben'}</span>
          </div>
        </div>
        {email && (
          <a className="mk-mail-btn" href={mailtoLink} draggable={false} onClick={abschicken}>
            Bewerbung per<br />E-Mail abschicken
          </a>
        )}
      </div>
      {email && (
        <p className="mk-attach-hint">
          Beim Abschicken werden Lebenslauf &amp; Anschreiben heruntergeladen — dann
          einfach aus dem Downloads-Ordner an die E-Mail anhängen.
        </p>
      )}
      {(mailGestartet || istMarkiert) && (
        istMarkiert ? (
          <div className="mk-erledigt">✓ Als abgeschickt markiert</div>
        ) : (
          <button
            type="button"
            className="mk-markieren-btn"
            onClick={() => { onAlsAbgeschickt?.(); setIstMarkiert(true); }}
          >
            Bewerbung als abgeschickt markieren
          </button>
        )
      )}
    </motion.div>
  );
}

export default function Bewerbungsmappe(props) {
  const [offen, setOffen] = useState(false);
  // langsam=true nur über den "Bewerbung abschicken"-Pfad (defaultOffen); dann klappt die
  // Mappe extra gemächlich auf. Manuelles Anklicken lässt langsam=false -> normale Dauer.
  const [langsam, setLangsam] = useState(false);
  // Die Staffelung macht jetzt Studio: defaultOffen wird erst wahr, wenn die rechte Leiste
  // fertig aufgefahren ist. Also hier keine eigene Wartezeit mehr — sofort langsam aufklappen.
  useEffect(() => {
    if (!props.defaultOffen) return;
    setLangsam(true);
    setOffen(true);
  }, [props.defaultOffen]);
  // Logo liegt im public-Ordner -> Maske inline setzen (Webpack darf den Pfad nicht auflösen).
  const logoMaske = `url(${process.env.PUBLIC_URL}/logo.png)`;

  return (
    <AnimatePresence initial={false}>
      {offen ? (
        <MappeOffen key="offen" {...props} langsam={langsam} onClose={() => setOffen(false)} />
      ) : (
        <div className="mappe" key="zu">
          <button
            type="button"
            className="mappe-folder"
            onClick={() => { setLangsam(false); setOffen(true); }}
            aria-label="Bewerbungsmappe öffnen"
          >
            <span className="mappe-folder-inner">
              <span className="mappe-paper mappe-paper-cv">Lebenslauf</span>
              <span className="mappe-paper mappe-paper-an">Anschreiben</span>
              <span className="mappe-back" />
              <span className="mappe-front">
                <span className="mappe-logo" style={{ WebkitMaskImage: logoMaske, maskImage: logoMaske }} />
              </span>
            </span>
            <span className="mappe-hint">Bewerbungsmappe</span>
          </button>
        </div>
      )}
    </AnimatePresence>
  );
}
